import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import EntityMemberSchema, { EntityMemberJSON, EntityMemberParams } from "_shared/entity-members/entity-member-schema";
import Guid from "_shared/guids/guid";
import { IComparableFieldEntryWithId, JSONType } from "work/proposals/redlining/field-redlines/field-redline";

export default class EntityMember implements IComparableFieldEntryWithId {
  public static get EXAMPLE(): EntityMember {
    return new EntityMember({
      legalEntityId: Guid.generate(),
      userId: Guid.generate(),
    });
  }

  public static create(params: EntityMemberParams): EntityMember {
    const parsed = EntityMemberSchema.validate(params);
    if (isNullOrUndefined(parsed)) {
      throw new InvalidEntityMemberJSON({
        json: params,
        message: "Invalid EntityMember parameters.",
      });
    }
    return new EntityMember({ ...params, ...parsed });
  }

  public static fromJSON(json: EntityMemberJSON | undefined | null): EntityMember | undefined | null {
    if (isNullOrUndefined(json)) return json;
    const parsed = EntityMemberSchema.deserialize(json);
    return new EntityMember({ ...parsed });
  }

  public static fromUnknownJSON(json: JSONType): EntityMember | null | undefined {
    if (isNullOrUndefined(json)) return json;
    else if (isValidEntityMemberJSON(json, { throw: true })) return EntityMember.fromJSON(json);
    throw new Error("Cannot create EntityMember from invalid JSON");
  }

  public static fromJSONArray(params: {
    jsonArray: EntityMemberJSON[] | null | undefined;
  }): EntityMember[] | null | undefined {
    const { jsonArray } = params;
    if (isNullOrUndefined(jsonArray)) return jsonArray;
    const paramsArray = EntityMemberSchema.deserializeArray(jsonArray);
    return paramsArray.map((params) => new EntityMember(params));
  }

  public static fromURLSearchParams(params: URLSearchParams): EntityMember | undefined {
    const userIdParam = params.get("tUserId");
    const entityIdParam = params.get("tEntityId");

    if (!userIdParam || !entityIdParam) {
      return undefined;
    }

    return new EntityMember({
      userId: new Guid(userIdParam),
      legalEntityId: new Guid(entityIdParam),
    });
  }

  public readonly legalEntityId: Guid;
  public readonly userId: Guid;

  public toJSON(): EntityMemberJSON {
    return EntityMemberSchema.serialize(this);
  }
  public get id(): Guid {
    return this.userId;
  }
  public get replacesId(): Guid | undefined {
    return this._replacesId;
  }
  public get isEmpty(): boolean {
    return false;
  }

  public constructor(values: EntityMemberParams) {
    const validatedParams = EntityMemberSchema.validate(values);
    this.legalEntityId = validatedParams.legalEntityId;
    this.userId = validatedParams.userId;
    this._replacesId = validatedParams.replacesId;
  }

  public toString(): string {
    return `EntityMember (
      legalEntityId: ${this.legalEntityId}
      userId: ${this.userId}
    )`;
  }

  public valueOf() {
    return {
      legalEntityId: this.legalEntityId.valueOf(),
      userId: this.userId.valueOf(),
    };
  }

  public isEqualTo(other: EntityMember | null | undefined): boolean {
    if (!other) return false;
    return this.legalEntityId.isEqualTo(other.legalEntityId) && this.userId.isEqualTo(other.userId);
  }

  public clone(): EntityMember {
    return new EntityMember({ ...this });
  }

  public replace(member: EntityMember): void {
    this._replacesId = member.id;
  }

  private _replacesId?: Guid;
}

export class InvalidEntityMemberJSON extends Error {
  public readonly json: JSONType;
  public readonly message: string = "Invalid EntityMemberJSON";

  constructor(args: { json: JSONType; message?: string }) {
    super(`${args.message}: ${JSON.stringify(args.json)}`);
    this.json = args.json;
  }
}

export class EntityMemberIndividualNotFoundError extends Error {}

function isValidEntityMemberJSON(json: JSONType, options?: { throw: boolean }): json is EntityMemberJSON {
  options = options ?? { throw: false };
  if (isNullOrUndefined(json)) return true;

  if (typeof json !== "object" || json === null) {
    if (options.throw) throw new InvalidEntityMemberJSON({ json });
    return false;
  }

  return true;
}
